import { useState } from 'react';
import type { MeResponse } from './AuthProvider';
import { SubscribeDialog } from './SubscribeDialog';
import { pastDueBannerText } from './billingNotice';

interface Props {
  me: MeResponse;
}

/**
 * Banner trial top-of-app (§17.6 + ajuste 1a "suscribible desde el día 1"):
 *   - daysLeft > 5       → banner NEUTRO con "Suscribirme" (solo si el admin
 *                          puede suscribir: SUPER_USER + kill-switch ON). Un
 *                          tenant no debe esperar a perder acceso para pagar.
 *   - 1..5 días           → banner amarillo ("Tu prueba termina en N días").
 *   - 0 días (hoy mismo)  → banner naranja (urgente).
 *   - Tenants legacy (trialEndsAt = null) o suscripción activa → null.
 * Prefiere `trialStatus` (derivado server-side); cae al cálculo client-side.
 * 1a: el botón abre el selector mensual/anual (SubscribeDialog) y de ahí el
 * Checkout de Stripe (sesión fresca + redirect). El CTA solo aparece si el
 * server habilitó el billing (me.billing.checkoutEnabled, FAIL-CLOSED — así
 * el paywall queda DORMIDO en prod hasta el go-live 1c).
 */
export function TrialBanner({ me }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const ts = me.tenant.trialStatus;
  // T4 (TyC §8.8) — cobro de suscripción rechazado: aviso, el acceso sigue activo.
  const pastDue = pastDueBannerText(me.tenant.billing, me.role === 'SUPER_USER');
  if (pastDue) {
    return (
      <div className="border-b border-rose-300 bg-rose-100 text-rose-900">
        <div className="mx-auto max-w-7xl px-4 py-2 text-xs font-medium md:px-6">{pastDue}</div>
      </div>
    );
  }
  // No mostramos banner si: suscripción activa (pagada) o tenant legacy (sin trial).
  if (ts?.active || me.tenant.subscriptionActive) return null;
  if (!me.tenant.trialEndsAt) return null;

  const ends = new Date(me.tenant.trialEndsAt).getTime();
  const now = Date.now();
  const daysLeft = ts?.daysLeft ?? Math.max(0, Math.ceil((ends - now) / (24 * 60 * 60 * 1000)));

  // Kill-switch server-side + rol: solo el admin ve el CTA de suscripción.
  const canSubscribe = !!me.billing?.checkoutEnabled && me.role === 'SUPER_USER';

  // Trial holgado (>5 días): banner discreto SOLO como punto de entrada a la
  // suscripción — sin CTA posible no aporta nada, no se muestra.
  if (daysLeft > 5 && !canSubscribe) return null;

  const tone = daysLeft === 0
    ? 'bg-orange-100 text-orange-900 border-orange-300'
    : daysLeft <= 5
      ? 'bg-amber-100 text-amber-900 border-amber-300'
      : 'bg-slate-100 text-slate-700 border-slate-200';

  const text = daysLeft === 0
    ? 'Tu prueba termina hoy. Configura tu plan antes de medianoche para no perder acceso.'
    : daysLeft <= 5
      ? `Te quedan ${daysLeft} día${daysLeft === 1 ? '' : 's'} de prueba. Configura tu plan para no perder acceso.`
      : `Estás en tu periodo de prueba (${daysLeft} días restantes). Puedes activar tu plan cuando quieras.`;

  return (
    <div className={`border-b ${tone}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 text-xs md:px-6">
        <span className="font-medium">{text}</span>
        {canSubscribe && (
          <button
            onClick={() => setDialogOpen(true)}
            className="rounded-md bg-white/70 px-3 py-1 text-[11px] font-medium text-slate-900 hover:bg-white"
          >
            {daysLeft > 5 ? 'Suscribirme' : 'Activar plan'}
          </button>
        )}
      </div>
      <SubscribeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
